import { Cancel } from '#base/Canvas/Cancel.js';
import { DesignGridWrapper } from '#base/Designer/index.js';
import { GroupToolView } from '#base/Designer/GroupToolView.js';
import { ToolView } from '#base/Designer/ToolView.js';
import { ToolKeys } from '#base/Designer/types.js';
import { CleanTextArea, ComboInput, CsvUpload, Font, ScrollToMe } from '#base/Text/Components.js';
import Button from '#base/ui/Button.js';
import GroupToolIcon from '#base/resources/icons/group/jersey-tool.svg';
import {
  DEFAULT_COLOR,
  isTextClean,
  JERSEY_NAME_PLACEHOLDER,
  JERSEY_NUMBER_PLACEHOLDER,
  TEXT_PLACEHOLDER,
} from '@c2s-npm/plumlib';
import React, { useEffect, useState } from 'react';

export const JerseySelect = ({
  fonts: assets,
  groups,
  onSelect,
  selected,
  cancel,
  revert,
  elements,
  hasPremium,
  graphicType,
  defaultColor,
}) => {
  const handleChange = (asset, { data, decoration, color }) => {
    onSelect({
      elementType: 'jersey',
      assetId: asset.key,
      fontName: asset.key,
      data,
      decoration,
      color,
    });
  };

  return (
    <ToolView
      {...{
        toolKey: ToolKeys.JERSEY,
        assets,
        groups,
        onSelect: handleChange,
        selected,
        cancel,
        revert,
        elements,
        hasPremium,
        renderInput: JerseyInput,
        renderAssets: JerseyAssets,
        placeholder: TEXT_PLACEHOLDER,
        graphicType,
        defaultColor,
      }}
    />
  );
};

const JerseyInput = ({ selected, setInput, commit, undo, canRevert, isNew, defaultColor }) => {
  const [text, setText] = useState(selected?.get?.('data') || '');
  const [decoration, setDecoration] = useState(selected?.get?.('decoration') || '');
  const color = selected?.get?.('color') || defaultColor || DEFAULT_COLOR;

  const handleTextChange = (e) => {
    const {
      target: { value },
    } = e;
    setText(value);
  };

  const handleNumberChange = (e) => {
    const {
      target: { value },
    } = e;
    setDecoration(value);
  };

  useEffect(() => {
    setInput({
      data: (isTextClean(text) && text) || JERSEY_NAME_PLACEHOLDER,
      decoration: (isTextClean(decoration) && decoration) || JERSEY_NUMBER_PLACEHOLDER,
      color,
    });
  }, [text, decoration, color]);

  return (
    <>
      <div className='t-flex t-w-full t-gap-3'>
        <div className='t-w-[calc(100%-82px)]'>
          <CleanTextArea
            value={text}
            placeholder={JERSEY_NAME_PLACEHOLDER}
            handleChange={handleTextChange}
            forceOneLine
            name='jersey_name'
          />
        </div>
        <div className='t-w-[70px]'>
          <CleanTextArea
            className='t-text-center'
            value={decoration}
            placeholder={JERSEY_NUMBER_PLACEHOLDER}
            handleChange={handleNumberChange}
            inputMode='numeric'
            name='jersey_number'
            forceOneLine
            autoFocus={false}
          />
        </div>
      </div>
      <div className='t-mt-3 t-text-center sm:t-relative sm:t-w-auto sm:t-bg-transparent'>
        <Cancel
          disabled={!text || !isTextClean(text) || !decoration || !isTextClean(decoration)}
          {...{ commit, undo, canRevert, isNew, label: 'Jersey' }}
        />
      </div>
    </>
  );
};

const getTitle = (view) => {
  if (view === 'input') {
    return 'Enter Names & Numbers';
  } else {
    return 'Jersey';
  }
};
const defaultData = [{ data: '', decoration: '', isDefault: true }];
export const JerseyGroupSelect = ({
  fonts: assets,
  groups,
  onSelect,
  selected,
  cancel,
  revert,
  elements,
  hasPremium,
  graphicType,
  defaultColor,
}) => {
  const handleChange = (asset, { data, color }) => {
    onSelect({
      isGroup: true,
      elementType: 'jersey',
      assetId: asset.key,
      fontName: asset.key,
      data,
      color,
    });
  };

  return (
    <GroupToolView
      {...{
        toolKey: ToolKeys.GROUP_JERSEY,
        image: GroupToolIcon,
        getTitle,
        assets,
        groups,
        onSelect: handleChange,
        selected,
        cancel,
        revert,
        elements,
        hasPremium,
        graphicType,
        defaultColor,
        renderInput: JerseyGroupInput,
        renderAssets: JerseyAssets,
      }}
    />
  );
};

const JerseyGroupInput = ({
  selected,
  input,
  setInput,
  commit,
  undo,
  canRevert,
  isNew,
  graphicType,
  defaultColor,
}) => {
  const [inputData, setData] = useState(selected?.get?.('data')?.toJS?.() || input?.data || defaultData);
  const color = selected?.get?.('color') || defaultColor || DEFAULT_COLOR;

  const handleChange = (i, v) => {
    const newData = [...inputData];
    newData.forEach((d) => delete d.active);
    newData[i] = Object.assign({ active: true }, newData[i], v);
    !!v && delete newData[i].isDefault;
    setData(newData);
  };

  const handleDelete = (i) => {
    const newData = [...inputData];
    newData.splice(i, 1);
    setData(newData);
  };

  const handleAdd = (n = 1) => {
    const newData = [
      ...inputData,
      ...(Array.isArray(n)
        ? n
        : Array.apply(null, Array(n)).map(() => ({
            data: '',
            decoration: '',
            active: true,
            isDefault: true,
          }))),
    ];
    newData.forEach((d) => delete d.active);
    newData[newData.length - 1].active = true;
    setData(newData);
  };

  useEffect(() => {
    setInput({
      data: inputData.map(({ data: text, decoration, ...d }) =>
        Object.assign(d, {
          data: (isTextClean(text) && text) || JERSEY_NAME_PLACEHOLDER,
          decoration: (isTextClean(decoration) && decoration) || JERSEY_NUMBER_PLACEHOLDER,
        })
      ),
      color: graphicType === 'engraved' ? null : color,
    });
  }, [inputData, color]);

  const inputText = inputData.find((d) => !!d.active)?.data || inputData[0]?.data || JERSEY_NAME_PLACEHOLDER;
  const inputNumber =
    inputData.find((d) => !!d.active)?.decoration || inputData[0]?.decoration || JERSEY_NUMBER_PLACEHOLDER;

  return (
    <>
      <MultiText
        data={inputData}
        onChange={handleChange}
        onDelete={handleDelete}
        onAdd={handleAdd}
        label={null}
        csvLabel='Jersey'
        uploadLabel='Add Names & Numbers'
        toolTitle='Jersey'
      />
      <div className='t-mt-5 t-text-center sm:t-relative sm:t-w-auto sm:t-bg-transparent'>
        <Cancel
          disabled={!inputText || !isTextClean(inputText) || !inputNumber || !isTextClean(inputNumber)}
          {...{ commit, undo, canRevert, isNew, label: 'Names & Numbers' }}
        />
      </div>
    </>
  );
};

const JerseyAssets = ({ input, assets, selectedAsset, setAsset, hasPremium }) => {
  const { data, decoration } = input;
  const text = Array.isArray(data) ? (data.find((d) => !!d.active) || data[0]).data : data;
  const number = Array.isArray(data) ? (data.find((d) => !!d.active) || data[0]).decoration : decoration;

  return (
    <>
      <DesignGridWrapper mode='grid' className='t-py-3'>
        {assets.map((asset, i) => (
          <Font
            key={i}
            font={asset}
            text={!!text && isTextClean(text) ? text : JERSEY_NAME_PLACEHOLDER}
            decoration={number || JERSEY_NUMBER_PLACEHOLDER}
            align={'center'}
            selected={selectedAsset === asset}
            onClick={setAsset}
            {...{ hasPremium }}
          />
        ))}
      </DesignGridWrapper>
    </>
  );
};

const MultiText = ({ placeholder, data, onChange, onDelete, onAdd, uploadLabel }) => {
  return (
    <DesignGridWrapper>
      <div className='multi-text'>
        {data.map((d, i) => (
          <TextRow
            key={i}
            data={d}
            placeholder={placeholder}
            onChange={(v) => onChange(i, v)}
            onDelete={() => onDelete(i)}
            canDelete={data.length > 1}
          />
        ))}
        <div className='t-mt-3 t-flex t-items-center t-justify-end t-gap-3'>
          <Button
            onClick={onAdd}
            size='xs'
            variant='shadowed-white'
            className='t-cursor-pointer t-whitespace-nowrap !t-font-medium !t-normal-case'>
            <i className='fa fa-fw fa-plus' /> Add Jersey
          </Button>
          <ComboInput onChange={onAdd} />
        </div>

        <div className='t-my-5 t-h-[1px] t-w-full t-bg-gray-400 t-text-center'>
          <span className='t-relative -t-top-3 t-inline-block t-bg-white t-px-2 t-py-1 t-text-xs t-font-normal t-text-brand-black'>
            OR
          </span>
        </div>

        <div className='t-mb-5 t-flex t-items-center t-gap-3'>
          <div className='t-flex t-w-full t-flex-auto t-items-center t-justify-center t-gap-2'>
            <CsvUpload
              onChange={onAdd}
              filename={`MyRTIC - Jersey Template.csv`}
              uploadLabelName={uploadLabel}
              fields={[
                { label: 'Name', value: 'data' },
                { label: 'Number', value: 'decoration' },
              ]}
            />
          </div>
        </div>
      </div>
    </DesignGridWrapper>
  );
};

const TextRow = ({ data, onChange, onDelete, canDelete }) => {
  return (
    <>
      {!data.data && <ScrollToMe />}
      <div className='t-mb-2 t-flex t-items-center t-gap-3'>
        <a onClick={() => onChange()} className='t-cursor-pointer'>
          <i className={'fa fa-fw fa-eye ' + (data.active ? '' : 'text-muted')} />
        </a>
        <div className='t-flex-auto'>
          <CleanTextArea
            value={data.data}
            placeholder={JERSEY_NAME_PLACEHOLDER}
            handleChange={(e) => onChange({ data: e.target.value })}
            onFocus={() => onChange()}
            type='text'
            name='jersey_name'
            forceOneLine
          />
        </div>
        <div className='t-flex-auto'>
          <CleanTextArea
            value={data.decoration}
            placeholder={JERSEY_NUMBER_PLACEHOLDER}
            handleChange={(e) => onChange({ decoration: e.target.value })}
            onFocus={() => onChange()}
            name='jersey_number'
            inputMode='numeric'
            type='text'
            forceOneLine
          />
        </div>
        {canDelete && (
          <a onClick={onDelete} className='t-cursor-pointer'>
            <i className='fa fa-fw fa-close' />
          </a>
        )}
      </div>
    </>
  );
};
